import { useCallback, useEffect, useRef, useState } from 'react';

import { Routes } from '../../../../models/routes';
import { csrfTokenHeader } from '../../../../services/CsrfTokenService';
import { OfferDraft } from '../../../../types';
import {
    AutoPriceAdjustmentMode,
    serializeOfferDraft,
} from '../__shared/offerDraft';

export type OfferDraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type Options = {
    drafts: OfferDraft[];
    editingDraftId: string | null;
    universeId: number | null;
    autoPriceMarkup: number;
    autoPriceAdjustmentMode: AutoPriceAdjustmentMode;
    autoPriceApplyToManual: boolean;
    initialBatchId?: number | null;
    initialRevision?: number;
};

type SaveResponse = {
    import_batch_id: number;
    revision: number;
};

const SAVE_DELAY_MS = 300;

export const useOfferDraftPersistence = ({
    drafts,
    editingDraftId,
    universeId,
    autoPriceMarkup,
    autoPriceAdjustmentMode,
    autoPriceApplyToManual,
    initialBatchId,
    initialRevision = 0,
}: Options) => {
    const [batchId, setBatchId] = useState<number | null>(
        initialBatchId ?? null,
    );
    const [status, setStatus] = useState<OfferDraftSaveStatus>('idle');
    const batchIdRef = useRef<number | null>(initialBatchId ?? null);
    const revisionRef = useRef(initialRevision);
    const latestRequestRevisionRef = useRef(initialRevision);
    const draftsRef = useRef(drafts);
    const editingDraftIdRef = useRef(editingDraftId);
    const universeIdRef = useRef(universeId);
    const autoPriceMarkupRef = useRef(autoPriceMarkup);
    const autoPriceAdjustmentModeRef = useRef(autoPriceAdjustmentMode);
    const autoPriceApplyToManualRef = useRef(autoPriceApplyToManual);
    const saveTimeoutRef = useRef<number | null>(null);
    const inFlightRequestsRef = useRef<Set<Promise<number | null>>>(new Set());
    const pausedRef = useRef(false);
    const mountedRef = useRef(true);

    draftsRef.current = drafts;
    editingDraftIdRef.current = editingDraftId;
    universeIdRef.current = universeId;
    autoPriceMarkupRef.current = autoPriceMarkup;
    autoPriceAdjustmentModeRef.current = autoPriceAdjustmentMode;
    autoPriceApplyToManualRef.current = autoPriceApplyToManual;

    const clearSaveTimeout = useCallback(() => {
        if (saveTimeoutRef.current === null) return;

        window.clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
    }, []);

    const performDraftSave = useCallback(
        async (keepalive = false): Promise<number | null> => {
            if (pausedRef.current) return batchIdRef.current;

            clearSaveTimeout();
            const revision = revisionRef.current + 1;
            revisionRef.current = revision;
            latestRequestRevisionRef.current = revision;

            if (mountedRef.current) setStatus('saving');

            try {
                const response = await fetch(Routes.SaveOfferDrafts, {
                    method: 'PATCH',
                    keepalive,
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        ...csrfTokenHeader(),
                    },
                    body: JSON.stringify({
                        import_batch_id: batchIdRef.current,
                        draft_revision: revision,
                        editing_draft_id: editingDraftIdRef.current,
                        universe_id: universeIdRef.current,
                        auto_price_markup: autoPriceMarkupRef.current,
                        auto_price_adjustment_mode:
                            autoPriceAdjustmentModeRef.current,
                        auto_price_apply_to_manual:
                            autoPriceApplyToManualRef.current,
                        offers: draftsRef.current.map(serializeOfferDraft),
                    }),
                });

                if (!response.ok) {
                    throw new Error('Не вдалося зберегти чернетку');
                }

                const payload = (await response.json()) as SaveResponse;
                batchIdRef.current = payload.import_batch_id;
                revisionRef.current = Math.max(
                    revisionRef.current,
                    payload.revision,
                );

                if (mountedRef.current) {
                    setBatchId(payload.import_batch_id);
                    if (latestRequestRevisionRef.current === revision) {
                        setStatus('saved');
                    }
                }
            } catch {
                if (
                    mountedRef.current &&
                    latestRequestRevisionRef.current === revision
                ) {
                    setStatus('error');
                }
            }

            return batchIdRef.current;
        },
        [clearSaveTimeout],
    );

    const persistDrafts = useCallback(
        (keepalive = false) => {
            const request = performDraftSave(keepalive).finally(() => {
                inFlightRequestsRef.current.delete(request);
            });
            inFlightRequestsRef.current.add(request);

            return request;
        },
        [performDraftSave],
    );

    useEffect(() => {
        if (pausedRef.current) return;

        clearSaveTimeout();
        if (mountedRef.current) setStatus('saving');
        saveTimeoutRef.current = window.setTimeout(() => {
            void persistDrafts();
        }, SAVE_DELAY_MS);

        return clearSaveTimeout;
    }, [
        autoPriceMarkup,
        autoPriceAdjustmentMode,
        autoPriceApplyToManual,
        clearSaveTimeout,
        drafts,
        editingDraftId,
        persistDrafts,
        universeId,
    ]);

    useEffect(() => {
        const persistBeforeUnload = () => {
            if (pausedRef.current) return;

            void persistDrafts(true);
        };

        window.addEventListener('pagehide', persistBeforeUnload);

        return () => {
            window.removeEventListener('pagehide', persistBeforeUnload);
        };
    }, [persistDrafts]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            clearSaveTimeout();

            if (!pausedRef.current) {
                void persistDrafts(true);
            }
        };
    }, [clearSaveTimeout, persistDrafts]);

    const flush = useCallback(async () => {
        clearSaveTimeout();
        await Promise.all(Array.from(inFlightRequestsRef.current));

        return persistDrafts();
    }, [clearSaveTimeout, persistDrafts]);
    const pause = useCallback(() => {
        pausedRef.current = true;
        clearSaveTimeout();
    }, [clearSaveTimeout]);
    const resume = useCallback(() => {
        pausedRef.current = false;
        void persistDrafts();
    }, [persistDrafts]);

    return { batchId, status, flush, pause, resume };
};
